import { DeviationRequestsListApi, DeviationRequestsQueryFilter, DeviationRequestsQueryRecord, DeviationRequestsQueryType } from 'api/deviation-requests-list';
import { autoinject } from 'aurelia-framework';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { RouteConfig, Router } from 'aurelia-router';
import { watch } from 'aurelia-watch-decorator';
import { tryExecute } from 'framework/error_handling/error-utils';
import { log } from 'framework/script_utilities/dev-decorators';
import { TaskCounter } from 'framework/script_utilities/task-executor';
import { TextHighlightValueConverter } from 'framework/value_converters/text-highlight';
import { DeviationRequestStatus } from 'model/business-enums';
import { QueryParams } from 'model/essential-types';
import { EnumsService } from 'services/enums-service';
import { requireServices } from 'support/activation';
import * as E from 'support/errors';
import { BATCH_SIZE, HOME_ROUTE } from 'support/settings';
import { QueryMemory } from 'support/shared_models/query-memory';
import { downloadFile } from 'framework/script_utilities/file-utils';

@autoinject
export class DeviationRequestsCommonList {
  queryType: DeviationRequestsQueryType = null;
  requests: DeviationRequestsQueryRecord[] = null;
  lastQueryId = 0;
  loaded = false;
  toolbarDownloadSubscription: Subscription = null;

  constructor(private enums: EnumsService, private api: DeviationRequestsListApi, private router: Router, public queryMemory: QueryMemory<DeviationRequestsQueryFilter>, private taskCounter: TaskCounter, private ea: EventAggregator) { }

  async canActivate() {
    await requireServices(this.enums);
  }

  @log('debug')
  activate(_params, route: RouteConfig) {
    this.queryType = route.settings.queryType as DeviationRequestsQueryType;
    this.toolbarDownloadSubscription = this.ea.subscribe('toolbar-download', () => this.download());
  }

  deactivate() {
    if (this.toolbarDownloadSubscription !== null)
      this.toolbarDownloadSubscription.dispose();
  }

  bind() {
    this.queryRequests();
  }

  filterMutated() {
    this.taskCounter.execute(() => tryExecute(() => this.queryRequests(), E.FILTER_ERROR_MESSAGE, E.FILTER_ERROR_TITLE, true));
  }

  @watch((x: DeviationRequestsCommonList) => x.queryMemory.sortExpression)
  sortExpressionChanged() {
    this.taskCounter.execute(() => tryExecute(() => this.queryRequests(), E.SORT_ERROR_MESSAGE, E.SORT_ERROR_TITLE, true));
  }

  async queryRequests() {
    const queryId = ++this.lastQueryId;
    const requests = await this.fetchRequests(0, this.batchSize);
    if (queryId === this.lastQueryId) {
      this.requests = requests;
      this.loaded = true;
    }
  }

  async fetchRequests(skip: number, take: number) {
    const highlight = this.queryMemory.filter.text;
    const params: QueryParams = { sortExpression: this.queryMemory.sortExpression, skip: skip, take: take };
    const requests = await this.api.queryDeviationRequests(this.queryType, this.queryMemory.filter, params);
    return requests.map(r => this.extendRequest(r, highlight));
  }

  private extendRequest(request: DeviationRequestsQueryRecord, expression: string) {
    const converter = new TextHighlightValueConverter();
    const highlight = (line: string) => converter.toView(line, expression)?.map(s => s.highlight ? `<span class="--highlight">${s.substring}</span>` : s.substring).join('');
    request['highlightedDetailInfo'] = highlight(request.detailInfo);
    request['highlightedDetailAdditionalInfo'] = highlight(request.detailAdditionalInfo);
    request['highlightedRequestedByName'] = highlight(request.requestedBy.name);
    request['highlightedAssignedToName'] = request.assignedTo !== null ? highlight(request.assignedTo.name) : null;
    request['travelersNames'] = request.travelers.map(t => highlight(t.name)).join(', ');
    request['travelersInitials'] = request.travelers.map(t => t.initials).join(', ');
    request['travelersTitle'] = request.travelers.map(t => t.name).join('\n');
    request['statusClass'] = `--${DeviationRequestStatus[request.status.value].toLowerCase()}`;
    request['statusDatePrefix'] = [DeviationRequestStatus.Rejected].includes(request.status.value) ? 'on' : 'since';
    return request;
  }

  private async download() {
    const params: QueryParams = { sortExpression: this.queryMemory.sortExpression, skip: null, take: null };
    const report = await this.api.downloadDeviationRequests(this.queryType, this.queryMemory.filter, params)
    const url = URL.createObjectURL(report.data);
    try { downloadFile(url, report.fileName); }
    finally { URL.revokeObjectURL(url); }
  }

  get homeRoute() {
    return HOME_ROUTE;
  }

  get batchSize() {
    return BATCH_SIZE;
  }

  showRequest(request: DeviationRequestsQueryRecord) {
    this.router.navigate(`#/${HOME_ROUTE}/deviationRequests/${request.id}`);
  }
}
